import React, { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { Mail, Lock, User as UserIcon, Phone, Eye, EyeOff, ArrowLeft, ArrowRight, Package, Palette, Truck, ShieldCheck, Star } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const Field = ({ icon: Icon, type = "text", value, onChange, placeholder, required, autoComplete, suffix }) => (
  <div className="relative">
    <Icon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      required={required}
      autoComplete={autoComplete}
      className="w-full pl-10 pr-10 py-3 rounded-lg bg-white border border-zinc-200 text-sm focus:border-rose-400 focus:ring-2 focus:ring-rose-100 outline-none transition-all"
    />
    {suffix && <div className="absolute right-3 top-1/2 -translate-y-1/2">{suffix}</div>}
  </div>
);

const AuthPage = () => {
  const { user, signIn, signUp, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("signin");
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [agree, setAgree] = useState(true);

  if (user) return <Navigate to="/account" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "signin") {
        await signIn({ email, password });
      } else {
        if (!agree) throw new Error("Please accept the Terms to continue");
        await signUp({ firstName, lastName, email, password, phone });
      }
      navigate("/account");
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const google = async () => {
    setBusy(true);
    setError("");
    try {
      await signInWithGoogle();
      navigate("/account");
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen flex bg-white">
      {/* LEFT — visual panel (hidden on mobile) */}
      <aside className="hidden lg:flex lg:w-[44%] xl:w-[48%] relative overflow-hidden bg-gradient-to-br from-rose-500 via-orange-500 to-amber-400 text-white">
        {/* Decorative blurred shapes */}
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-32 -left-20 w-[460px] h-[460px] rounded-full bg-white/15 blur-3xl" />
          <div className="absolute bottom-0 -right-20 w-[380px] h-[380px] rounded-full bg-rose-200/30 blur-3xl" />
          <div className="absolute top-1/3 right-1/4 w-3 h-3 rounded-full bg-white/60" />
          <div className="absolute top-1/2 left-1/3 w-2 h-2 rounded-full bg-white/50" />
          <div className="absolute bottom-1/4 left-12 w-2.5 h-2.5 rounded-full bg-white/70" />
        </div>

        <div className="relative flex flex-col justify-between p-10 xl:p-14 w-full">
          {/* top: back link */}
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-medium text-white/90 hover:text-white transition-colors w-fit">
            <ArrowLeft className="w-4 h-4" /> Back to dplus.ai
          </Link>

          {/* middle: stacked print mockups + headline */}
          <div className="max-w-md">
            {/* Stacked cards visual */}
            <div className="relative h-44 mb-8 hidden xl:block">
              <div className="absolute left-0 top-6 w-48 h-28 rounded-xl bg-white/95 shadow-2xl rotate-[-8deg] p-4 flex flex-col justify-between">
                <div>
                  <div className="text-[10px] tracking-widest uppercase text-rose-500 font-bold">Design Plus</div>
                  <div className="text-zinc-900 font-bold text-sm mt-1 leading-tight">Avery Reyes<br/><span className="font-normal text-zinc-500 text-[11px]">Creative Director</span></div>
                </div>
                <div className="text-[10px] text-zinc-400">orders@dplus.ai</div>
              </div>
              <div className="absolute left-24 top-0 w-48 h-28 rounded-xl bg-zinc-900 shadow-2xl rotate-[6deg] p-4 text-white flex flex-col justify-between">
                <div className="text-[10px] tracking-widest uppercase text-rose-300 font-bold">VIP Card</div>
                <div className="font-bold text-sm leading-tight">Reorder in<br/>one tap.</div>
              </div>
              <div className="absolute left-44 top-8 w-44 h-28 rounded-xl bg-gradient-to-br from-amber-200 to-rose-200 shadow-2xl rotate-[-3deg] p-4">
                <div className="text-[10px] tracking-widest uppercase text-rose-700 font-bold">Premium</div>
                <div className="text-rose-900 font-bold text-base mt-1 leading-tight">Save 30%<br/>this month</div>
              </div>
            </div>

            <span className="inline-block text-[11px] uppercase tracking-[0.25em] font-bold bg-white/15 backdrop-blur px-3 py-1.5 rounded-full">
              Your Design Plus Account
            </span>
            <h1 className="text-4xl xl:text-5xl font-bold leading-tight tracking-tight mt-5">
              Print once.<br />Reorder forever.
            </h1>
            <p className="text-white/85 mt-4 text-base leading-relaxed max-w-sm">
              Save your designs, track every order and unlock one-tap reorders from any device.
            </p>

            <ul className="mt-7 space-y-3 max-w-sm">
              {[
                { icon: Package, t: "Real-time order tracking across all your jobs" },
                { icon: Palette, t: "Print-ready designs saved for instant reuse" },
                { icon: Truck, t: "Faster checkout with saved addresses & payment" },
              ].map((f) => (
                <li key={f.t} className="flex items-start gap-3 text-sm">
                  <div className="w-7 h-7 rounded-full bg-white/15 backdrop-blur flex items-center justify-center shrink-0 mt-0.5">
                    <f.icon className="w-3.5 h-3.5" />
                  </div>
                  <span className="text-white/90 leading-relaxed pt-1">{f.t}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* bottom: trust stat */}
          <div className="flex items-center gap-3 text-xs text-white/80">
            <div className="flex">
              {[...Array(5)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-amber-200 text-amber-200" />)}
            </div>
            <span>4.9 from 500+ Broward businesses</span>
          </div>
        </div>
      </aside>

      {/* RIGHT — form */}
      <section className="flex-1 flex flex-col bg-white">
        {/* Top bar for mobile + brand mark */}
        <div className="flex items-center justify-between px-5 sm:px-10 py-5 border-b border-zinc-100 lg:border-0 lg:hidden">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-zinc-600 hover:text-rose-500 transition-colors font-medium">
            <ArrowLeft className="w-4 h-4" /> Back to dplus.ai
          </Link>
        </div>

        <div className="flex-1 flex items-center justify-center px-5 sm:px-10 py-10">
          <div className="w-full max-w-md">
            <h2 className="text-3xl font-bold text-zinc-900 tracking-tight">
              {mode === "signin" ? "Welcome back" : "Create your account"}
            </h2>
            <p className="text-sm text-zinc-500 mt-2">
              {mode === "signin"
                ? "Sign in to your Design Plus account."
                : "It takes under a minute — and unlocks one-tap reorders."}
            </p>

            {/* Tabs */}
            <div className="grid grid-cols-2 gap-1 mt-7 p-1 bg-zinc-100 rounded-lg">
              <button onClick={() => setMode("signin")} className={`text-sm font-semibold py-2 rounded-md transition-colors ${mode === "signin" ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-500 hover:text-zinc-700"}`}>Sign in</button>
              <button onClick={() => setMode("signup")} className={`text-sm font-semibold py-2 rounded-md transition-colors ${mode === "signup" ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-500 hover:text-zinc-700"}`}>Create account</button>
            </div>

            <button onClick={google} disabled={busy} className="w-full flex items-center justify-center gap-3 border border-zinc-200 bg-white px-4 py-3 rounded-lg text-sm font-semibold text-zinc-800 hover:bg-zinc-50 hover:border-zinc-300 transition-colors mt-5 disabled:opacity-60">
              <svg viewBox="0 0 48 48" className="w-4 h-4">
                <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
                <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
                <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
                <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
              </svg>
              Continue with Google
            </button>

            <div className="relative my-5">
              <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-zinc-200" /></div>
              <div className="relative flex justify-center text-xs"><span className="bg-white px-3 text-zinc-400 font-medium uppercase tracking-wider">Or with email</span></div>
            </div>

            <form onSubmit={submit} className="space-y-3">
              {mode === "signup" && (
                <div className="grid grid-cols-2 gap-3">
                  <Field icon={UserIcon} value={firstName} onChange={setFirstName} placeholder="First name" required autoComplete="given-name" />
                  <Field icon={UserIcon} value={lastName} onChange={setLastName} placeholder="Last name" autoComplete="family-name" />
                </div>
              )}
              <Field icon={Mail} type="email" value={email} onChange={setEmail} placeholder="you@business.com" required autoComplete="email" />
              {mode === "signup" && (
                <Field icon={Phone} type="tel" value={phone} onChange={setPhone} placeholder="Phone (optional)" autoComplete="tel" />
              )}
              <Field
                icon={Lock}
                type={showPwd ? "text" : "password"}
                value={password}
                onChange={setPassword}
                placeholder="Password"
                required
                autoComplete={mode === "signin" ? "current-password" : "new-password"}
                suffix={
                  <button type="button" onClick={() => setShowPwd((s) => !s)} className="text-zinc-400 hover:text-zinc-700">
                    {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                }
              />

              {mode === "signin" ? (
                <div className="flex justify-end">
                  <button type="button" onClick={() => alert("A reset link would be emailed once the backend is live.")} className="text-xs font-semibold text-rose-500 hover:underline">Forgot password?</button>
                </div>
              ) : (
                <label className="flex items-start gap-2 text-xs text-zinc-600 leading-relaxed cursor-pointer pt-1">
                  <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-0.5 accent-rose-500" />
                  <span>I agree to the <Link to="/terms" className="text-rose-500 hover:underline">Terms</Link> and <Link to="/privacy" className="text-rose-500 hover:underline">Privacy Policy</Link>.</span>
                </label>
              )}

              {error && (
                <div className="bg-rose-50 border border-rose-200 text-rose-700 text-xs font-medium px-3 py-2 rounded-lg">{error}</div>
              )}

              <button disabled={busy} className="w-full bg-zinc-900 text-white py-3 rounded-lg text-sm font-bold hover:bg-rose-500 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 mt-1">
                {busy ? "Please wait…" : mode === "signin" ? "Sign in" : "Create account"}
                {!busy && <ArrowRight className="w-4 h-4" />}
              </button>
            </form>

            <p className="text-center text-xs text-zinc-500 mt-5">
              {mode === "signin" ? (
                <>New to Design Plus? <button onClick={() => setMode("signup")} className="text-rose-500 font-semibold hover:underline">Create an account</button></>
              ) : (
                <>Already have an account? <button onClick={() => setMode("signin")} className="text-rose-500 font-semibold hover:underline">Sign in instead</button></>
              )}
            </p>

            <div className="mt-7 flex items-center justify-center gap-2 text-[11px] text-zinc-400 border-t border-zinc-100 pt-5">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />
              Your data is encrypted in transit.
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default AuthPage;
